/**
 * @file index.tsx
 *
 * @fileoverview Renders blog teaser.
 */
import { ReactNode } from "react";
import Badge from "../../../02-atoms/03-badge";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import Spacing from "../../../02-atoms/06-layout/07-spacing";
import Box from "../../../02-atoms/07-box";
import Text from "../../../02-atoms/13-text/03-text";
import styles from "./blogTeaser.module.scss";

interface BlogTeaserProps {
  /**
   * The blog title.
   */
  title: string | ReactNode;
  /**
   * The title tag.
   *
   * @default 'h2'
   */
  titleTag?: string;
  /**
   * The blog banner.
   *
   */
  banner?: ReactNode;
  /**
   * The blog content.
   *
   */
  content?: string | ReactNode;
  /**
   * The blog actions positioned bottom right.
   *
   */
  actions?: ReactNode;
  /**
   * The blog categories.
   *
   * @default []
   */
  categories?: string[];
  /**
   * The advertorial flag.
   */
  advertorialLabel?: string;
}

export default function BlogTeaser({
  title,
  titleTag = "h2",
  banner,
  content,
  actions,
  categories = [],
  advertorialLabel,
}: BlogTeaserProps) {
  return (
    <Box paddingClean className={styles.blogTeaserBox}>
      <div className={styles.boxTeaserWrapper}>
        <div className={styles.blogTeaserBanner}>{banner}</div>
        <FlexItems flexDirection="column">
          {advertorialLabel && (
            <FlexItem alignSelf="end">
              <div className={styles.advertorialBadge}>{advertorialLabel}</div>
            </FlexItem>
          )}
          <FlexItem flexGrow={1}>
            <Spacing
              paddingRight={8}
              paddingLeft={8}
              paddingTop={advertorialLabel ? 0 : 4}
              paddingBottom={4}
            >
              <Text tag={titleTag} className={styles.blogTeaserTitle}>
                {title}
              </Text>
              <Spacing size="medium" />
              <Text tag="div" className={styles.blogTeaserText}>
                {content}
              </Text>
            </Spacing>
          </FlexItem>
          <FlexItem>
            <Spacing
              paddingRight={8}
              paddingLeft={8}
              paddingTop={4}
              paddingBottom={4}
            >
              <FlexItems
                justifyContent="space-between"
                alignItems="center"
                flexWrap="nowrap"
                gap={6}
              >
                <FlexItem>
                  <FlexItems gap={4}>
                    {categories.map((category, index) => (
                      <Badge
                        className={styles.category}
                        badgeStyle="primary"
                        key={index}
                      >
                        {category}
                      </Badge>
                    ))}
                  </FlexItems>
                </FlexItem>
                <FlexItem>{actions}</FlexItem>
              </FlexItems>
            </Spacing>
          </FlexItem>
        </FlexItems>
      </div>
    </Box>
  );
}

export { BlogTeaser };
